import React, { useState } from 'react';
import { ThemeSettings } from './DashboardLayout';

interface SettingsPanelProps {
  onClose: () => void;
  theme: ThemeSettings;
  onThemeChange: (theme: ThemeSettings) => void;
}

const presetColors = [
  { name: 'BC Blue', primary: '#003366', secondary: '#0066CC' },
  { name: 'Forest Green', primary: '#065F46', secondary: '#10B981' },
  { name: 'Royal Purple', primary: '#5B21B6', secondary: '#8B5CF6' },
  { name: 'Ocean Teal', primary: '#0F766E', secondary: '#14B8A6' },
  { name: 'Sunset Orange', primary: '#C2410C', secondary: '#F97316' },
  { name: 'Burgundy', primary: '#881337', secondary: '#E11D48' },
];

const fontOptions = [
  "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  "'Arial', sans-serif",
  "'Georgia', serif",
  "'Courier New', monospace",
  "'Trebuchet MS', sans-serif",
];

const fontSizeOptions = [
  { label: 'Small', value: '14px' },
  { label: 'Medium', value: '16px' },
  { label: 'Large', value: '18px' },
  { label: 'Extra Large', value: '20px' },
];

const SettingsPanel: React.FC<SettingsPanelProps> = ({ onClose, theme, onThemeChange }) => {
  const [localTheme, setLocalTheme] = useState(theme);

  const handleSave = () => { onThemeChange(localTheme); onClose(); };

  const handleReset = () => {
    const defaultTheme: ThemeSettings = {
      primaryColor: '#003366', secondaryColor: '#0066CC',
      fontSize: '16px', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    };
    setLocalTheme(defaultTheme);
    onThemeChange(defaultTheme);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-start z-[1000] p-8 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl mt-8" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 rounded-t-lg" style={{ backgroundColor: localTheme.primaryColor }}>
          <h2 className="text-white text-xl font-bold m-0">⚙️ Settings & Customization</h2>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer p-1 flex items-center rounded hover:bg-white/20 transition-colors">
            <svg width="24" height="24" fill="white" viewBox="0 0 24 24">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">

          {/* Color Presets */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Color Themes</h3>
            <p className="text-sm text-gray-500 mb-4">Choose a preset color scheme</p>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
              {presetColors.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => setLocalTheme({ ...localTheme, primaryColor: preset.primary, secondaryColor: preset.secondary })}
                  className="rounded-lg overflow-hidden cursor-pointer bg-white transition-all"
                  style={{ border: localTheme.primaryColor === preset.primary ? `3px solid ${preset.primary}` : '2px solid #E5E7EB' }}
                >
                  <div className="h-8" style={{ backgroundColor: preset.primary }} />
                  <div className="h-8" style={{ backgroundColor: preset.secondary }} />
                  <span className="block p-1.5 text-xs font-medium text-gray-600">{preset.name}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Custom Colors */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Custom Colors</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { label: 'Primary Color', key: 'primaryColor' as const },
                { label: 'Secondary Color', key: 'secondaryColor' as const },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-600 mb-2">{label}</label>
                  <div className="flex gap-2 items-center">
                    <input type="color" value={localTheme[key]}
                      onChange={e => setLocalTheme({ ...localTheme, [key]: e.target.value })}
                      className="w-14 h-10 border border-gray-200 rounded cursor-pointer" />
                    <input type="text" value={localTheme[key]}
                      onChange={e => setLocalTheme({ ...localTheme, [key]: e.target.value })}
                      className="flex-1 px-2 py-2 border border-gray-200 rounded text-sm" />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Typography */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Typography</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-600 mb-2">Font Family</label>
              <select value={localTheme.fontFamily}
                onChange={e => setLocalTheme({ ...localTheme, fontFamily: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded text-base cursor-pointer"
                style={{ fontFamily: localTheme.fontFamily }}>
                {fontOptions.map(font => (
                  <option key={font} value={font} style={{ fontFamily: font }}>
                    {font.split(',')[0].replace(/'/g, '')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Font Size</label>
              <div className="grid grid-cols-4 gap-2">
                {fontSizeOptions.map(opt => (
                  <button key={opt.value}
                    onClick={() => setLocalTheme({ ...localTheme, fontSize: opt.value })}
                    className="py-2 rounded font-medium border-none cursor-pointer transition-colors"
                    style={{
                      backgroundColor: localTheme.fontSize === opt.value ? localTheme.primaryColor : '#F3F4F6',
                      color: localTheme.fontSize === opt.value ? 'white' : '#374151',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Preview */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Preview</h3>
            <div className="rounded-lg p-6 text-white" style={{ backgroundColor: localTheme.primaryColor, fontFamily: localTheme.fontFamily, fontSize: localTheme.fontSize }}>
              <div className="flex justify-between items-center mb-3">
                <h4 className="m-0 text-lg">Sample Header</h4>
                <button className="px-4 py-2 border-none text-white rounded cursor-pointer font-medium" style={{ backgroundColor: localTheme.secondaryColor }}>
                  Sample Button
                </button>
              </div>
              <p className="m-0 leading-relaxed opacity-90">
                This is how your text will appear with the selected settings.
              </p>
            </div>
          </section>

          {/* Important Links */}
          <section>
            <h3 className="text-lg font-bold text-gray-900 mb-4">Important Links</h3>
            <div className="flex flex-col gap-3">
              {[
                { href: 'https://www.welcomebc.ca', label: '🌐 WelcomeBC - Official Newcomer Resources' },
                { href: 'https://www2.gov.bc.ca', label: '🏛️ BC Government Services' },
                { href: 'https://www.canada.ca/en/immigration-refugees-citizenship.html', label: '🍁 Immigration, Refugees and Citizenship Canada' },
                { href: 'tel:211', label: '📞 BC 211 - Community Services Helpline' },
              ].map(({ href, label }) => (
                <a key={href} href={href} target="_blank" rel="noopener noreferrer"
                  className="block px-4 py-3 bg-gray-100 rounded-lg no-underline text-gray-800 font-medium hover:bg-gray-200 transition-colors text-sm">
                  {label}
                </a>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
          <button onClick={handleReset}
            className="px-6 py-2.5 border border-red-500 bg-white text-red-500 rounded font-medium cursor-pointer hover:bg-red-50 transition-colors">
            Reset to Default
          </button>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-6 py-2.5 border border-gray-200 bg-white text-gray-600 rounded font-medium cursor-pointer hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave}
              className="px-6 py-2.5 border-none text-white rounded font-medium cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: localTheme.primaryColor }}>
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
